import { queryOptions } from "@tanstack/react-query";
import type { components } from "@/api/schema";
import type { QuoteBuilderApi } from "../api/client";
import { keepNewestRevision } from "../api/queries";
import type { Quote, QuoteWorkflow } from "../api/types";

type Schemas = components["schemas"];

export type ShippingState = Schemas["QbShippingRead"];
export type Shipment = Schemas["QbShipmentRead"];
export type ShippingMethod = Schemas["QbShippingMethodRead"];
export type ShippingMethodId = ShippingMethod["id"];
export type ShipmentSelections = Schemas["QbShipmentSelections"];
export type FreightOffer = Schemas["QbFreightOfferRead"];
export type DeliveryAddress = ShippingState["delivery_address"];
export type DeliveryContact = ShippingState["delivery_contact"];
/** A PO's own ship-to (USA-310); ``null`` ships to the quote's delivery address. */
export type ShipTo = Schemas["QbShipmentShipTo"];

/** What the Shipping stage save accepts (`QbShippingDataUpdate`); every field is a patch. */
export type ShippingChanges = {
  selected_method?: ShippingMethodId | null;
  notes?: string | null;
  delivery_address?: DeliveryAddress;
  delivery_contact?: DeliveryContact;
  refresh_delivery?: boolean;
  /** Quote-wide delivery choices: named fields change, ``null`` goes back to detection. */
  delivery_defaults?: Partial<ShipmentSelections>;
  shipment_selections?: Array<
    Partial<ShipmentSelections> & {
      manufacturer_id: number;
      ship_to?: ShipTo | null;
    }
  >;
  freight_override?: number | null;
};

export const shippingQueryOptions = (api: QuoteBuilderApi, quoteId: string) =>
  queryOptions({
    queryKey: ["quote-shipping", quoteId],
    queryFn: ({ signal }) =>
      api.request<ShippingState>(`/quotes/${quoteId}/shipping`, { signal }),
    structuralSharing: keepNewestRevision,
    enabled: Boolean(quoteId),
  });

export function saveShipping(
  api: QuoteBuilderApi,
  quote: Pick<Quote, "id" | "revision">,
  changes: ShippingChanges,
) {
  return api.request<QuoteWorkflow>(`/quotes/${quote.id}/workflow/shipping`, {
    method: "PUT",
    body: JSON.stringify({ expected_revision: quote.revision, data: changes }),
  });
}
